import type { GroupRole, LabRole, PharmacyRole, Role, RoleKind } from 'types/models';
import type { CoreResource } from 'types/models/core_resource';
import type { CoreResourceRole, RoleWithResource } from 'types/models/role';

// The current acting context is a Lab/Pharmacy/Group you can navigate under — the Faks org role
// (super_admin) is a normal role in the list but carries no dashboard context to switch into.
export const isResourceScopedRole = (
  role: RoleWithResource
): role is Exclude<RoleWithResource, CoreResourceRole & { resource: CoreResource }> =>
  role.resource_type !== 'Core::Resource';

export const isGroupRole = (role: Role): role is GroupRole => role.resource_type === 'Group';

export const isLabRole = (role: Role): role is LabRole => role.resource_type === 'Lab';

export const isPharmacyRole = (role: Role): role is PharmacyRole =>
  role.resource_type === 'Pharmacy';

export const isPharmacyOwner = (role?: RoleKind): boolean => 'pharmacy_owner' === role;

export const isSalesRep = (role?: RoleKind): boolean => 'lab_sales_rep' === role;

export const isSalesRepNoAccess = (role?: RoleKind): boolean => 'lab_sales_rep_no_access' === role;

// Sales reps are mobile-only: the web lab pages answer them with a 403.
export const hasNoWebAccess = (role?: RoleKind): boolean =>
  isSalesRep(role) || isSalesRepNoAccess(role);

export const isLabAdmin = (role?: RoleKind): boolean => 'lab_admin' === role;

// A role on the Faks org specifically. A Core::Resource can host other orgs, so match the name too.
export const isFaksRole = (
  role: RoleWithResource
): role is CoreResourceRole & { resource: CoreResource } =>
  role.resource_type === 'Core::Resource' && role.resource.name === 'Faks';

export const hasSuperAdminRole = (roles: Pick<Role, 'kind'>[]): boolean =>
  roles.some((role) => role.kind === 'super_admin');

// A super admin also holds the admin role, so a faks admin is simply anyone with the admin role.
export const hasAdminRole = (roles: Pick<Role, 'kind'>[]): boolean =>
  roles.some((role) => role.kind === 'admin');

export const hasValidatorRole = (roles: Pick<Role, 'kind'>[]): boolean =>
  roles.some((role) => role.kind === 'validator');

export const hasOnboarderRole = (roles: Pick<Role, 'kind'>[]): boolean =>
  roles.some((role) => role.kind === 'onboarder');

export const shouldShowLabUserPicker = (roleKind: RoleKind): boolean =>
  roleKind === 'lab_admin' || roleKind === 'lab_regional_director';
